import { HttpStatusCode } from "axios";
import ICaseStudy from "../models/caseStudy/ICaseStudy";
import networkService from "./NetworkService";
import ICaseStudyPreview from "../models/caseStudy/ICaseStudyPreview";
import clientService from "./ClientService";

class CaseStudyService {

    /**
     * @description Gets all case studies
     */
    async getCaseStudies(): Promise<ICaseStudy[]> {
        const result = await networkService.get<ICaseStudy[]>("/portfolio/case-studies");

        if (!result.isSuccessful) {
            throw result.data;
        }

        if (result.status === HttpStatusCode.NoContent) {
            return [];
        }

        const caseStudies = (result.data as ICaseStudy[])
            .sort((a, b) => a.title.localeCompare(b.title));

        return caseStudies;
    }

    /**
     * @description Gets previews of all case studies
     */
    async getCaseStudyPreviews(): Promise<ICaseStudyPreview[]> {
        const result = await networkService.get<ICaseStudyPreview[]>("/portfolio/case-studies/previews");

        if (!result.isSuccessful) {
            throw result.data;
        }

        if (result.status === HttpStatusCode.NoContent) {
            return [];
        }

        const previews = result.data as ICaseStudyPreview[];
        const [caseStudies, clients] = await Promise.all([
            this.getCaseStudies(),
            clientService.getClients()
        ]);
        const caseStudiesById = new Map(caseStudies.map((caseStudy) => [caseStudy.id, caseStudy]));
        const clientsById = new Map(clients.map((client) => [client.id, client]));

        const resolvedPreviews = previews
            .map((preview) => {
                const fallbackClientId = caseStudiesById.get(preview.id)?.clientId;
                const client = clientsById.get(
                    preview.clientId ?? fallbackClientId ?? ''
                );

                if (!client) {
                    console.warn("Case study preview has no matching client", {
                        caseStudyId: preview.id,
                        title: preview.title,
                        previewClientId: preview.clientId,
                        fallbackClientId
                    });
                    return null;
                }

                return { ...preview, client };
            })
            .filter((preview): preview is ICaseStudyPreview => preview !== null)
            .sort((a, b) => {
                const clientComparison = a.client.name.localeCompare(b.client.name);
                return clientComparison !== 0 ? clientComparison : a.title.localeCompare(b.title);
            });

        return resolvedPreviews;
    }

    /**
     * @description Gets the specified case study
     * @param {string} arg The ID or slug of the case study to be returned
     */
    async getCaseStudy(arg: string): Promise<ICaseStudy> {
        const result = await networkService.get<ICaseStudy>(`/portfolio/case-study/${arg}`);

        if (result.isSuccessful) {
            return result.data as ICaseStudy;
        }

        throw result.data;
    }

    /**
     * @description Gets a preview the specified case study
     * @param {string} id The ID or slug of the case study to be returned
     */
    async getCaseStudyPreview(id: string): Promise<ICaseStudyPreview> {
        const result = await networkService.get<ICaseStudyPreview>(`/portfolio/case-study/${id}/preview`);

        if (result.isSuccessful) {
            const preview = result.data as ICaseStudyPreview;
            const clientId = preview.clientId ?? (await this.getCaseStudy(id)).clientId;
            const client = await clientService.getClient(clientId);

            return {
                ...preview,
                client
            };
        }

        throw result.data;
    }

    /**
     * @description Creates a new case study
     * @param {ICaseStudy} caseStudy The case study data to be created
     */
    async createCaseStudy(caseStudy: ICaseStudy): Promise<string> {
        const result = await networkService.put<ICaseStudy, string>(`/portfolio/case-study`, caseStudy);

        if (result.isSuccessful) {
            return result.data as string;
        }

        throw result.data;
    }

    /**
     * @description Partially updates the specified case study
     * @param {ICaseStudy} caseStudy The partial case study data to be updated
     */
    async updateCaseStudy(caseStudy: ICaseStudy): Promise<ICaseStudy> {
        const result = await networkService.patch<ICaseStudy, ICaseStudy>(`/portfolio/case-study`, caseStudy);

        if (result.isSuccessful) {
            return result.data as ICaseStudy;
        }

        throw result.data;
    }

    /**
     * @description Deletes the specified case study
     * @param {string} id The ID of the case study to be deleted
     */
    async deleteCaseStudy(id: string, industry: string): Promise<boolean> {
        const result = await networkService.delete<void>(`/portfolio/case-study/${id}`, {
            "X-INDUSTRY": industry
        });
        
        return result.isSuccessful;
    }

    /**
     * @description Gets all case study categories
     */
    async getCategories(): Promise<string[]> {
        const result = await networkService.get<string[]>(`/portfolio/case-studies/categories`);

        if (!result.isSuccessful) {
            throw result.data;
        }

        return result.status !== HttpStatusCode.NoContent
            ? result.data as string[]
            : [];
    }
}

const caseStudyService = new CaseStudyService();
export default caseStudyService;